import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog";
import { XCircle, Loader2 } from "lucide-react";

/**
 * CancelBookingDialog — student-facing confirmation to cancel a booking.
 * Fetches /bookings/{id}/cancel-quote on open so the student sees exactly what
 * is refunded (merchandise) and what is forfeited (labor deposit) before confirming.
 */
export function CancelBookingDialog({ booking, onCanceled }) {
  const [open, setOpen] = useState(false);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get(`/bookings/${booking.booking_id}/cancel-quote`)
      .then((r) => setQuote(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Could not load cancel quote"))
      .finally(() => setLoading(false));
  }, [open, booking.booking_id]);

  const confirm = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/bookings/${booking.booking_id}/cancel`);
      toast.success(data.message || "Booking canceled");
      setOpen(false);
      onCanceled?.(booking.booking_id, data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel booking");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          data-testid={`cancel-btn-${booking.booking_id}`}
          className="rounded-full border-destructive/30 text-destructive hover:bg-destructive/10 hover:text-destructive"
        >
          <XCircle className="w-4 h-4 mr-1" /> Cancel
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid={`cancel-dialog-${booking.booking_id}`}>
        <AlertDialogHeader>
          <AlertDialogTitle>Cancel this booking?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="text-charcoal/70 text-sm space-y-3">
              {loading && (
                <div className="flex items-center gap-2 text-charcoal/60">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading refund details…
                </div>
              )}
              {quote && (
                <>
                  <div className="rounded-lg border border-[#1F2A40] bg-[#0B1220] p-3 space-y-1.5 text-charcoal">
                    <p className="flex justify-between text-sm">
                      <span>Labor deposit (non-refundable)</span>
                      <span className="text-destructive font-medium" data-testid={`cancel-forfeit-${booking.booking_id}`}>
                        −${Number(quote.labor_deposit_forfeit || 0).toFixed(2)}
                      </span>
                    </p>
                    {quote.merchandise_amount > 0 && (
                      <p className="flex justify-between text-sm">
                        <span>Merchandise refund {quote.merch_refund_eligible ? "(minus 25% processing)" : "(inside 24h — no refund)"}</span>
                        <span className={quote.merch_refund_eligible ? "text-sage font-medium" : "text-charcoal/50"} data-testid={`cancel-refund-${booking.booking_id}`}>
                          {quote.merch_refund_eligible ? `+$${Number(quote.merchandise_refund_amount).toFixed(2)}` : "$0.00"}
                        </span>
                      </p>
                    )}
                    <p className="text-[11px] text-charcoal/50">
                      Hours until service: {quote.hours_until_service !== null ? `${quote.hours_until_service}h` : "—"}
                    </p>
                  </div>
                  <p>Are you sure you want to cancel? This can&apos;t be undone.</p>
                </>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`cancel-dialog-back-${booking.booking_id}`}>Keep booking</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); confirm(); }}
            disabled={submitting || loading}
            data-testid={`cancel-dialog-confirm-${booking.booking_id}`}
            className="bg-destructive hover:bg-destructive/90 text-white disabled:opacity-50"
          >
            {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Canceling…</>) : "Cancel booking"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
